import { Scene, Vector3 } from '@babylonjs/core';

export class ImageUtils {
  constructor(public scene: Scene) {}

  /**
   * Loads an image from a url
   *
   * @param imageUrl
   * @returns
   */
  static async loadImage(imageUrl: string): Promise<HTMLImageElement> {
    const imgBlob: Blob = await fetch(imageUrl).then((response) => {
      return response.blob();
    });
    const imageBlobUrl = URL.createObjectURL(imgBlob);
    const image = await ImageUtils.imageFromUrl(imageBlobUrl);
    return image;
  }

  static blobUrlFromArrayBuffer(buffer: ArrayBuffer): string {
    return URL.createObjectURL(new Blob([buffer]));
  }

  /**
   * Creates an image from a  url
   *
   * @param url
   * @returns image
   */
  static async imageFromUrl(url: string): Promise<HTMLImageElement> {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  }

  /**
   * Maps colors from a grayscale value to a color map
   * Uses linear interpolation between the two nearest color stops
   *
   * @param gray
   * @param colorMap
   * @returns
   */
  static mapColors(gray: number, colorMap?: Map<number, number[]>): number[] {
    if (!colorMap) {
      // Define the default color map
      colorMap = new Map();
      colorMap.set(0, [0, 0, 255]);
      colorMap.set(64, [0, 255, 255]);
      colorMap.set(128, [0, 255, 0]);
      colorMap.set(192, [255, 255, 0]);
      colorMap.set(254, [255, 0, 0]);
    }

    let color = [0, 0, 0];
    let prev = 0;
    let next = 255;

    // Find the two nearest color stops in the color map
    for (let key of colorMap.keys()) {
      if (gray >= key && key >= prev) {
        prev = key;
        color = colorMap.get(key);
      } else if (gray <= key && key <= next) {
        next = key;
        color = colorMap.get(key);
      }
    }

    // Interpolate between the two nearest color stops
    const t = (gray - prev) / (next - prev);
    const r = color[0] * t + colorMap.get(prev)[0] * (1 - t);
    const g = color[1] * t + colorMap.get(prev)[1] * (1 - t);
    const b = color[2] * t + colorMap.get(prev)[2] * (1 - t);

    return [r, g, b];
  }

  /**
   * Convert a heightmap image to a mix map image
   *
   * @param image
   * @param colorMap
   * @returns mix map image
   */
  static async generateMixMapFromHeightMap(image: HTMLImageElement, colorMap?: Map<number, number[]>): Promise<HTMLImageElement> {
    //  read image pixel by pixel
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0);
    const imageData = context.getImageData(0, 0, image.width, image.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3]; // alpha
      const height = (r + g + b) / 3;

      //color grading
      const colorValues = this.mapColors(height, colorMap);
      data[i] = colorValues[0];
      data[i + 1] = colorValues[1];
      data[i + 2] = colorValues[2];
      data[i + 3] = a;
    }
    context.putImageData(imageData, 0, 0);

    const rgbImage = await ImageUtils.imageFromUrl(canvas.toDataURL());

    canvas.remove();

    return rgbImage;
  }

  /**
   * Paint a path onto a mix map image using the green channel.
   * The green channel controls diffuseTexture2 in the TerrainMaterial,
   * so setting it to 255 will show the path texture on the terrain.
   *
   * Uses multi-pass drawing for soft edge transitions.
   *
   * @param mixMapImage The existing mix map image
   * @param pathPoints The path points in world space
   * @param terrainWidth Width of the terrain ground mesh
   * @param terrainHeight Height (depth) of the terrain ground mesh
   * @param pathWidth Width of the path in world units
   * @returns Modified mix map image with path painted
   */
  static async paintPathOnMixMap(
    mixMapImage: HTMLImageElement,
    pathPoints: Vector3[],
    terrainWidth: number,
    terrainHeight: number,
    pathWidth: number
  ): Promise<HTMLImageElement> {
    const canvas = document.createElement('canvas');
    canvas.width = mixMapImage.width;
    canvas.height = mixMapImage.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(mixMapImage, 0, 0);

    // Step 1: Strip green channel from entire mix map and redistribute to R/B.
    // This reserves the green channel exclusively for the path texture.
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      if (g > 0) {
        const rb = r + b;
        if (rb > 0) {
          // Redistribute green proportionally to R and B
          data[i] = Math.min(255, Math.round(r + g * (r / rb)));
          data[i + 2] = Math.min(255, Math.round(b + g * (b / rb)));
        } else {
          // No R or B → split green evenly
          data[i] = Math.round(g / 2);
          data[i + 2] = Math.round(g / 2);
        }
        data[i + 1] = 0; // Clear green
      }
    }
    ctx.putImageData(imageData, 0, 0);

    // Step 2: Paint path in green channel
    // Convert world space path width to pixel width
    const pixelWidth = (pathWidth / terrainWidth) * canvas.width;

    /**
     * Convert world coordinates to pixel coordinates on the mix map.
     * The ground mesh goes from -width/2 to +width/2 in X and -height/2 to +height/2 in Z.
     * Image pixel (0,0) is top-left.
     */
    const worldToPixel = (worldX: number, worldZ: number): [number, number] => {
      const u = (worldX + terrainWidth / 2) / terrainWidth;
      const v = 1 - (worldZ + terrainHeight / 2) / terrainHeight;
      return [u * canvas.width, v * canvas.height];
    };

    /** Draw the full path polyline with given style */
    const drawPath = (lineWidth: number, color: string) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      for (let i = 0; i < pathPoints.length; i++) {
        const [px, py] = worldToPixel(pathPoints[i].x, pathPoints[i].z);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
    };

    // Multi-pass drawing for soft edge transitions
    // Outer edge: wide, very transparent
    drawPath(pixelWidth * 2.0, 'rgba(0, 255, 0, 0.15)');
    // Middle edge: medium, semi-transparent
    drawPath(pixelWidth * 1.4, 'rgba(0, 255, 0, 0.3)');
    // Solid core: path width, full green
    drawPath(pixelWidth, 'rgb(0, 255, 0)');

    const result = await ImageUtils.imageFromUrl(canvas.toDataURL());
    canvas.remove();
    return result;
  }

  static async generateCubeTextureImages2(imageUrl: string): Promise<string[]> {
    const image = await ImageUtils.imageFromUrl(imageUrl);

    const columns = 4;
    const rows = 3;

    const width = image.width / columns;
    const height = image.height / rows;
    const cells: Map<number, number[]> = new Map();
    cells.set(0, [1]);
    cells.set(1, [0, 1, 2, 3]);
    cells.set(2, [1]);

    const rotated: Map<string, (context: CanvasRenderingContext2D) => void> = new Map();
    rotated.set('01', (context: CanvasRenderingContext2D) => {
      context.translate(0, height);
      context.rotate(-Math.PI / 2);
    });
    rotated.set('21', (context: CanvasRenderingContext2D) => {
      context.translate(width, 0);
      context.rotate(Math.PI / 2);
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    const urls: string[] = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < columns; c++) {
        if (!cells.get(r).includes(c)) {
          continue;
        }
        const sx = c * width;
        const sy = r * height;

        const rotateKey = r + '' + c;
        if (rotated.has(rotateKey)) {
          rotated.get(rotateKey)(context);
        }

        context.drawImage(image, sx, sy, width, height, 0, 0, width, height);

        urls.push(canvas.toDataURL());

        if (rotated.has(rotateKey)) {
          context.resetTransform();
        }

        context.clearRect(0, 0, width, height);
      }
    }

    canvas.remove();

    //mapping: "_px", "_py", "_pz", "_nx", "_ny", "_nz"
    const mappedUrls: string[] = [];
    mappedUrls[0] = urls[2];
    mappedUrls[1] = urls[0];
    mappedUrls[2] = urls[1];
    mappedUrls[3] = urls[4];
    mappedUrls[4] = urls[5];
    mappedUrls[5] = urls[3];

    return mappedUrls;
  }

  static async generateCubeTextureImages(imageUrl: string): Promise<string[]> {
    const image = await ImageUtils.imageFromUrl(imageUrl);

    const columns = 4;
    const rows = 3;

    const width = image.width / columns;
    const height = image.height / rows;

    // Standard cross layout:
    // Row 0:        [py]          (col 1)
    // Row 1: [nx]   [pz]  [px]  [nz]  (col 0-3)
    // Row 2:        [ny]          (col 1)
    const facePositions: { name: string; row: number; col: number }[] = [
      { name: 'py', row: 0, col: 1 },
      { name: 'nx', row: 1, col: 0 },
      { name: 'pz', row: 1, col: 1 },
      { name: 'px', row: 1, col: 2 },
      { name: 'nz', row: 1, col: 3 },
      { name: 'ny', row: 2, col: 1 },
    ];

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    const faceUrls: Record<string, string> = {};

    for (const face of facePositions) {
      const sx = face.col * width;
      const sy = face.row * height;

      context.clearRect(0, 0, width, height);
      context.drawImage(image, sx, sy, width, height, 0, 0, width, height);

      faceUrls[face.name] = canvas.toDataURL();
    }

    canvas.remove();

    // BabylonJS CubeTexture.CreateFromImages expects: [px, py, pz, nx, ny, nz]
    return [faceUrls['px'], faceUrls['py'], faceUrls['pz'], faceUrls['nx'], faceUrls['ny'], faceUrls['nz']];
  }
}
